import * as React from "react";
import { omit } from "ramda";
import { Icon, IconButton, Selection, type IObjectWithKey } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { createUuid, type UUID } from "../../utils/uuid";
import type { UserGroupSortKey, UserGroup } from "../../api";
import {
    type UserGroupsApiResource,
    type UserGroupsQuery,
} from "../../domain/repositories/repository-user-groups";
import { observer } from "mobx-react";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import { ListSearchField } from "../atoms/list-search-field";
import type { SearchState } from "../../utils/search-state";

import type { SelectionState } from "../../utils/selection-state";
import { PanelUserGroupDetails } from "./panel-user-group-details";
import css from "./list-user-groups.scss";
import { defaultPageSize } from "../../utils/constants";

export interface ListUserGroupsProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly vehicleGroupIds?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
    readonly ignoreUrl?: boolean;
    readonly showSearchField?: boolean;
}

interface ListUserGroupItem extends IObjectWithKey {
    readonly label: string;
    readonly key: UUID;
    readonly createdAt: string;
    readonly assignedUserCount: number;
    readonly assignedVehicleGroupCount: number;
}

import { store } from "../../store";

@observer
export class ListUserGroups extends React.Component<ListUserGroupsProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    @observable private accessor detailsPanelVisible: boolean = false;
    @observable private accessor detailsPanelUserGroupId: UUID | undefined;

    constructor(props: ListUserGroupsProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.userGroups.initializeList(this.listStateId, {
            query: () => this.query,
            ignoreUrl: this.props.ignoreUrl ?? false,
        });
    }

    @computed private get listStateId(): UUID {
        return this.props?.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<UserGroupSortKey> {
        return store.serviceListStates.userGroups.getSortState(this.listStateId);
    }

    @computed private get searchState(): SearchState {
        return store.serviceListStates.userGroups.getSearchState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<UserGroupsApiResource> {
        return store.serviceListStates.userGroups.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.userGroups.getSelectionState(this.listStateId);
    }

    @computed private get query(): UserGroupsQuery {
        return {
            pageSize: defaultPageSize,
            vehicleGroupIds: this.props.vehicleGroupIds ?? [],
            ...this.sortState.query,
            ...this.searchState.query,
        };
    }

    @computed private get userGroups(): (UserGroup | undefined)[] {
        if (this.props.ids) {
            return this.props.ids.map((id) => store.repositoryUserGroups.byId(id));
        }
        const group = store.repositoryUserGroups.byQuery(
            this.query,
            this.paginationState.pagination,
        );
        return group;
    }

    @computed private get items(): (ListUserGroupItem | undefined)[] {
        return this.userGroups.map((userGroup) => {
            if (!userGroup) {
                return;
            }
            return {
                label: userGroup.label,
                key: userGroup.id,
                createdAt: store.i18n.formatDateOnly(userGroup.createdAt),
                assignedUserCount: userGroup.assignedUserCount,
                assignedVehicleGroupCount: userGroup.assignedVehicleGroupCount,
            };
        });
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    @action.bound private openDetailsPanel(item: ListUserGroupItem): void {
        this.detailsPanelUserGroupId = item.key;
        this.detailsPanelVisible = true;
    }

    @action.bound private closeDetailsPanel(): void {
        this.detailsPanelVisible = false;
        this.detailsPanelUserGroupId = undefined;
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.userGroups.isInitialized(this.listStateId)) {
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);

        // In the group details panel we always show the full list of user
        // groups and so we disable the search functionality.
        let searchField;
        if (this.props.showSearchField) {
            searchField = (
                <ListSearchField
                    placeholder={store.i18n.t("listUserGroups.search.placeholder")}
                    listStateId={this.listStateId}
                    listState={store.serviceListStates.userGroups}
                />
            );
        }

        return (
            <>
                <LayoutList
                    paginationState={this.paginationState}
                    commandBar={this.props.commandBar}
                    searchField={searchField}
                >
                    <ElofleetList
                        {...props}
                        selection={this.selection}
                        onColumnHeaderClick={this.sortState.toggleColumn}
                        columns={this.sortState.patchColumns([
                            {
                                fieldName: "detailsButton",
                                name: "",
                                key: "detailsButton",
                                minWidth: 20,
                                maxWidth: 20,
                                onRender: (item, _index) => {
                                    return (
                                        <IconButton
                                            className={css.detailsButton__button}
                                            onClick={() => this.openDetailsPanel(item)}
                                        >
                                            <Icon
                                                iconName="ChevronRight"
                                                className={css.detailsButton__icon}
                                            />
                                        </IconButton>
                                    );
                                },
                            },

                            {
                                fieldName: "label",
                                name: store.i18n.t("listUserGroups.column.label.name"),
                                key: "label",
                                minWidth: 200,
                                maxWidth: 300,
                            },
                            {
                                fieldName: "assignedUserCount",
                                name: store.i18n.t("listUserGroups.column.assignedUserCount.name"),
                                key: "assignedUserCount",
                                minWidth: 80,
                                maxWidth: 100,
                            },
                            {
                                fieldName: "assignedVehicleGroupCount",
                                name: store.i18n.t(
                                    "listUserGroups.column.assignedVehicleGroupCount.name",
                                ),
                                key: "assignedVehicleGroupCount",
                                minWidth: 100,
                                maxWidth: 150,
                            },
                        ])}
                        items={this.items}
                    />
                </LayoutList>
                <PanelUserGroupDetails
                    isOpen={this.detailsPanelVisible}
                    userGroupId={this.detailsPanelUserGroupId}
                    onDismiss={this.closeDetailsPanel}
                />
            </>
        );
    }
}
